import React, { useContext, useEffect, useState } from "react";
import { AuthUserSession, currentUserPoolUser, signOut as amplifySignOut } from "../../config/cognito-authenticator";
import { authorizedRequest } from "../../utils/api-request";
import { Manager } from "../../types/typescript-axios";

export type ManagerContextType = AuthUserSession & Partial<Pick<Manager, 'id' | 'authority' | 'personalDataFlg'>> & {
  isApiChecked: boolean
}
export const ManagerContext = React.createContext<[
  ManagerContextType|null,
  (manager: AuthUserSession|null) => Promise<void>,
  () => Promise<void>
]>([null, (_) => new Promise((res) => res), async () => {}])

export const ManagerProvider: React.FunctionComponent = ({ children }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [manager, setManager] = useState<ManagerContextType|null>(null)

  useEffect(() => {
    setCurrentManager()
  }, [])

  async function setCurrentManager() {
    const current = await currentUserPoolUser()
    if (!current) return setIsLoading(false)

    await setManagerContext(current)
  }

  async function signOut() {
    await amplifySignOut()
    setManager(null)
  }

  async function setManagerContext(authUser: AuthUserSession | null) {
    if (!authUser) {
      setManager(null)
      return
    }

    if (authUser.needsNewPassword) {
      setManager({
        ...authUser,
        isApiChecked: false,
      })
      setIsLoading(false)
      return
    }

    const managerContextType: ManagerContextType = {
      ...authUser,
      isApiChecked: false,
    }

    const response = await authorizedRequest(authUser, 'ShowMe')
    if (response.status === 200) {
      managerContextType.id = response.data.manager.id
      managerContextType.authority = response.data.manager.authority
      managerContextType.personalDataFlg = response.data.manager.personalDataFlg
      managerContextType.isApiChecked = true
    }

    setManager(managerContextType)
    setIsLoading(false)
  }

  if (isLoading) return (<div></div>)

  return (
    <ManagerContext.Provider value={[manager, setManagerContext, signOut]}>
      { children }
    </ManagerContext.Provider>
  )
}

export function useManager(): ManagerContextType {
  const [manager] = useContext(ManagerContext)
  return manager as ManagerContextType
}
